import { InfoCircleIcon } from 'foundations-assets/icons/components/InfoCircleIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { PPUIGrid } from '../grid/PPUIGrid';
import { SingleSkeletonLoader } from '../loading/SingleSkeletonLoader';
import { PriceDisplay } from '../price/PriceDisplay';
import { FSC_TIER_NAMES } from './fscTierNames';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
/* Fixed width for the section-label column so product names line up across
 * every group (Plan / Seats / Credits). Sized to fit the widest label across
 * locales; a label wider than this only pushes the name column in, it never
 * clips. */
const LABEL_COLUMN_WIDTH = '5rem';
/* Minimum comfortable width for the name+price block. When the label column
 * plus this minimum can't fit the card width, the block wraps to its own row
 * below the label (stacked layout). This is the knob that sets the reflow
 * breakpoint. */
const NAME_BLOCK_MIN = '13rem';
const OrderSummarySection = ({
  children
}) => /*#__PURE__*/_jsx(View, {
  borderBlockStartStyle: "solid",
  borderBlockStartColor: "border-core-subtle",
  borderBlockStartWidth: "100",
  paddingBlockStart: "400",
  children: /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    gap: "100",
    children: children
  })
});
const OrderSummaryGroup = ({
  label,
  children
}) => /*#__PURE__*/_jsxs(Flex, {
  flexWrap: "wrap",
  columnGap: "600",
  rowGap: "200",
  children: [/*#__PURE__*/_jsx(View, {
    flexGrow: "1",
    flexBasis: LABEL_COLUMN_WIDTH,
    children: /*#__PURE__*/_jsx(Text, {
      as: "p",
      typography: "heading-50",
      color: "text-core-default",
      children: label
    })
  }), /*#__PURE__*/_jsx(View, {
    flexGrow: "999",
    flexBasis: "0",
    minInlineSize: NAME_BLOCK_MIN,
    children: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "100",
      children: children
    })
  })]
});
const MonthlyAmount = ({
  amount,
  currencyCode,
  mode
}) => /*#__PURE__*/_jsxs(_Fragment, {
  children: [mode === 'manage' && '+', /*#__PURE__*/_jsx(PriceDisplay, {
    price: amount,
    currencyCode: currencyCode
  }), /*#__PURE__*/_jsx(FormattedMessage, {
    message: "fscConfigure.orderSummary.perMonth"
  })]
});
const PricedLineItem = ({
  label,
  labelTestId,
  amount,
  currencyCode,
  mode
}) => /*#__PURE__*/_jsxs(PPUIGrid, {
  gridTemplateColumns: "minmax(0, 1fr) max-content",
  columnGap: "400",
  alignItems: "start",
  children: [/*#__PURE__*/_jsx(Text, {
    as: "p",
    typography: "detail-200",
    "data-test-id": labelTestId,
    children: label
  }), /*#__PURE__*/_jsx(Text, {
    as: "p",
    typography: "detail-200",
    textWrap: "nowrap",
    children: /*#__PURE__*/_jsx(MonthlyAmount, {
      amount: amount,
      currencyCode: currencyCode,
      mode: mode
    })
  })]
});
const LineItemRow = ({
  item,
  currencyCode,
  mode
}) => {
  var _item$label, _item$label2;
  switch (item.type) {
    case 'seat':
      return /*#__PURE__*/_jsx(PricedLineItem, {
        label: (_item$label = item.label) !== null && _item$label !== void 0 ? _item$label : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "fscConfigure.orderSummary.seatLineItem",
          options: {
            quantity: item.quantity,
            name: item.name
          }
        }),
        amount: item.amount,
        currencyCode: currencyCode,
        mode: mode
      });
    case 'includedCredits':
      return /*#__PURE__*/_jsx(Text, {
        as: "p",
        typography: "detail-200",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "fscConfigure.orderSummary.includedCredits",
          options: {
            credits: /*#__PURE__*/_jsx(FormattedNumber, {
              value: item.credits
            })
          }
        })
      });
    case 'additionalCredits':
      return /*#__PURE__*/_jsx(PricedLineItem, {
        label: (_item$label2 = item.label) !== null && _item$label2 !== void 0 ? _item$label2 : /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "fscConfigure.orderSummary.additionalCredits",
          options: {
            credits: /*#__PURE__*/_jsx(FormattedNumber, {
              value: item.credits
            }),
            packName: item.packName
          }
        }),
        labelTestId: "order-summary-additional-credits",
        amount: item.amount,
        currencyCode: currencyCode,
        mode: mode
      });
    // no default
  }
};
const OrderSummaryLineItemsSection = ({
  label,
  items,
  currencyCode,
  mode
}) => /*#__PURE__*/_jsx(OrderSummarySection, {
  children: /*#__PURE__*/_jsx(OrderSummaryGroup, {
    label: label,
    children: items.map(item => /*#__PURE__*/_jsx(LineItemRow, {
      item: item,
      currencyCode: currencyCode,
      mode: mode
    }, item.id))
  })
});
const SkeletonRows = ({
  count
}) => /*#__PURE__*/_jsx(_Fragment, {
  children: Array.from({
    length: count
  }, (_, index) => /*#__PURE__*/_jsx(SingleSkeletonLoader, {
    height: 16
  }, index))
});
const resolveEstimatedLabel = (mode, billingTerm) => {
  if (billingTerm.type === 'annual') {
    return mode === 'manage' ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "fscConfigure.orderSummary.estimatedAnnualChanges"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "fscConfigure.orderSummary.estimatedAnnualBilling"
    });
  }
  return mode === 'manage' ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "fscConfigure.orderSummary.estimatedMonthlyChanges"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "fscConfigure.orderSummary.estimatedMonthlyBilling"
  });
};
const OrderSummaryReadyBody = ({
  content,
  totalLabel
}) => {
  const {
    tier,
    currencyCode,
    total,
    billingTerm,
    mode
  } = content;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(OrderSummarySection, {
      children: /*#__PURE__*/_jsx(OrderSummaryGroup, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "fscConfigure.orderSummary.plan"
        }),
        children: /*#__PURE__*/_jsx(Text, {
          as: "p",
          typography: "detail-200",
          children: FSC_TIER_NAMES[tier]
        })
      })
    }), content.groups.seats.length > 0 && /*#__PURE__*/_jsx(OrderSummaryLineItemsSection, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "fscConfigure.orderSummary.seats"
      }),
      items: content.groups.seats,
      currencyCode: currencyCode,
      mode: mode
    }), content.groups.credits.length > 0 && /*#__PURE__*/_jsx(OrderSummaryLineItemsSection, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "fscConfigure.orderSummary.credits"
      }),
      items: content.groups.credits,
      currencyCode: currencyCode,
      mode: mode
    }), /*#__PURE__*/_jsx(OrderSummarySection, {
      children: /*#__PURE__*/_jsxs(PPUIGrid, {
        gridTemplateColumns: "minmax(0, 1fr) max-content",
        columnGap: "400",
        alignItems: "center",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "100",
          minInlineSize: "0",
          children: [totalLabel !== null && totalLabel !== void 0 ? totalLabel : /*#__PURE__*/_jsx(Text, {
            as: "p",
            typography: "heading-50",
            children: resolveEstimatedLabel(mode, billingTerm)
          }), /*#__PURE__*/_jsx(Tooltip, {
            use: "longform",
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "fscConfigure.orderSummary.estimatedCostTooltip"
            }),
            placement: "top",
            children: /*#__PURE__*/_jsx(Flex, {
              as: "span",
              display: "inline-flex",
              alignItems: "center",
              flexShrink: "0",
              role: "img",
              "aria-label": unescapedText('fscConfigure.orderSummary.estimatedCostTooltipIconAriaLabel'),
              children: /*#__PURE__*/_jsx(InfoCircleIcon, {})
            })
          })]
        }), total > 0 && /*#__PURE__*/_jsx(Text, {
          as: "p",
          typography: "heading-50",
          textWrap: "nowrap",
          children: /*#__PURE__*/_jsx(MonthlyAmount, {
            amount: total,
            currencyCode: currencyCode,
            mode: mode
          })
        })]
      })
    })]
  });
};
const OrderSummaryLoadingBody = ({
  previous
}) => {
  var _previous$groups$seat, _previous$groups$cred;
  const seatCount = (_previous$groups$seat = previous === null || previous === void 0 ? void 0 : previous.groups.seats.length) !== null && _previous$groups$seat !== void 0 ? _previous$groups$seat : 0;
  const creditCount = (_previous$groups$cred = previous === null || previous === void 0 ? void 0 : previous.groups.credits.length) !== null && _previous$groups$cred !== void 0 ? _previous$groups$cred : 0;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(OrderSummarySection, {
      children: /*#__PURE__*/_jsx(OrderSummaryGroup, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "fscConfigure.orderSummary.plan"
        }),
        children: /*#__PURE__*/_jsx(SingleSkeletonLoader, {
          height: 16,
          width: "120px"
        })
      })
    }), /*#__PURE__*/_jsx(OrderSummarySection, {
      children: /*#__PURE__*/_jsx(OrderSummaryGroup, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "fscConfigure.orderSummary.seats"
        }),
        children: /*#__PURE__*/_jsx(SkeletonRows, {
          count: seatCount || 1
        })
      })
    }), /*#__PURE__*/_jsx(OrderSummarySection, {
      children: /*#__PURE__*/_jsx(OrderSummaryGroup, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "fscConfigure.orderSummary.credits"
        }),
        children: /*#__PURE__*/_jsx(SkeletonRows, {
          count: creditCount || 1
        })
      })
    }), /*#__PURE__*/_jsx(OrderSummarySection, {
      children: /*#__PURE__*/_jsx(SingleSkeletonLoader, {
        height: 20
      })
    })]
  });
};
const OrderSummaryMessage = ({
  children
}) => /*#__PURE__*/_jsx(OrderSummarySection, {
  children: /*#__PURE__*/_jsx(Text, {
    as: "p",
    typography: "detail-200",
    color: "text-core-subtle",
    children: children
  })
});
const OrderSummaryBody = ({
  content,
  totalLabel
}) => {
  switch (content.status) {
    case 'ready':
      return /*#__PURE__*/_jsx(OrderSummaryReadyBody, {
        content: content,
        totalLabel: totalLabel
      });
    case 'loading':
      return /*#__PURE__*/_jsx(OrderSummaryLoadingBody, {
        previous: content.previous
      });
    case 'empty':
      return /*#__PURE__*/_jsx(OrderSummaryMessage, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "fscConfigure.orderSummary.empty"
        })
      });
    case 'error':
      return /*#__PURE__*/_jsx(OrderSummaryMessage, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "fscConfigure.orderSummary.error"
        })
      });
    // no default
  }
};
export const FscOrderSummary = ({
  content,
  currencyPicker,
  checkoutCta,
  totalLabel
}) => /*#__PURE__*/_jsx(CardWrapper, {
  inlineSize: "100%",
  padding: "600",
  borderRadius: "400",
  children: /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "400",
    children: [/*#__PURE__*/_jsxs(Flex, {
      justifyContent: "space-between",
      alignItems: "center",
      gap: "300",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        typography: "heading-50",
        marginBlockStart: "0",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "fscConfigure.orderSummary.title"
        })
      }), currencyPicker]
    }), /*#__PURE__*/_jsx(OrderSummaryBody, {
      content: content,
      totalLabel: totalLabel
    }), checkoutCta]
  })
});